import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { type ClinicalTemplateFormState, clinicalTemplateFormSchema, labelsFromText } from './template-utils';

const ALL_VISIBILITY_LABELS_VALUE = '__all_visibility_labels__';

interface ClinicalTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  form: ClinicalTemplateFormState;
  onSubmit: (form: ClinicalTemplateFormState) => void;
  isSaving: boolean;
  showCancelButton?: boolean;
  showVisibilityLabels?: boolean;
  visibilityLabelOptions?: string[];
  showGlobalFlags?: boolean;
}

export function ClinicalTemplateDialog({
  open,
  onOpenChange,
  title,
  description,
  form,
  onSubmit,
  isSaving,
  showCancelButton = true,
  showVisibilityLabels = false,
  visibilityLabelOptions = [],
  showGlobalFlags = false,
}: ClinicalTemplateDialogProps) {
  const formMethods = useForm<ClinicalTemplateFormState>({
    resolver: zodResolver(clinicalTemplateFormSchema),
    defaultValues: form,
    mode: 'onChange',
  });
  const { control, formState, handleSubmit, reset, trigger } = formMethods;

  useEffect(() => {
    reset(form);
    void trigger();
  }, [form, reset, trigger]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Form {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nafn *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lýsing</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="template_content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sniðmát *</FormLabel>
                  <FormControl>
                    <Textarea {...field} className="min-h-[180px] font-mono text-sm" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="example_note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Dæmi (valfrjálst)</FormLabel>
                  <FormControl>
                    <Textarea {...field} className="min-h-[120px] font-mono text-sm" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="custom_prompt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sérsniðin fyrirmæli (valfrjálst)</FormLabel>
                  <FormControl>
                    <Textarea {...field} className="min-h-[90px]" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {showVisibilityLabels && (
              <FormField
                control={control}
                name="visibility_labels"
                render={({ field }) => {
                  const selectedLabels = labelsFromText(field.value);
                  const options = Array.from(new Set([...visibilityLabelOptions, ...selectedLabels])).sort();
                  const selectedValue = selectedLabels[0] || ALL_VISIBILITY_LABELS_VALUE;

                  return (
                    <FormItem>
                      <FormLabel>Merki/Hlutverk</FormLabel>
                      <Select
                        value={selectedValue}
                        onValueChange={(value) => field.onChange(value === ALL_VISIBILITY_LABELS_VALUE ? '' : value)}
                      >
                        <FormControl>
                          <SelectTrigger aria-label="Merki/Hlutverk">
                            <SelectValue placeholder="Allir í stofnun" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={ALL_VISIBILITY_LABELS_VALUE}>Allir í stofnun</SelectItem>
                          {options.map((label) => (
                            <SelectItem key={label} value={label}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            )}

            {showGlobalFlags && (
              <div className="flex flex-wrap gap-6">
                <FormField
                  control={control}
                  name="is_default"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(checked) => field.onChange(checked === true)}
                        />
                      </FormControl>
                      <FormLabel>Sjálfgefið</FormLabel>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="auto_copy_for_new_users"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(checked) => field.onChange(checked === true)}
                        />
                      </FormControl>
                      <FormLabel>Afrita sjálfkrafa fyrir nýja notendur</FormLabel>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <DialogFooter>
              {showCancelButton && (
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Hætta við
                </Button>
              )}
              <Button type="submit" disabled={isSaving || !formState.isValid}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Vista
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
